import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";
import type { CustomerCredit, CustomerCreditsSummary } from "@/api.types.v2";

const API = import.meta.env.VITE_API_URL;

/**
 * #262: customer-side voucher wallet listing.
 *
 * `status=available` returns unredeemed, non-expired rows.
 * `status=redeemed` returns rows that have been used.
 * Expired rows are filtered out server-side for non-admin callers.
 */
export function useCustomerCredits(
  status: "available" | "redeemed" = "available"
) {
  const { token, customer } = useSession();

  return useQuery<CustomerCredit[], Error>({
    queryKey: ["customer-credits", customer?.id, status],
    enabled: !!token && !!customer?.id,
    queryFn: async () => {
      const res = await axios.get<{ data: CustomerCredit[] }>(
        `${API}/api/v2/customers/me/credits?status=${status}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data.data;
    },
  });
}

/**
 * Aggregated outstanding wallet value — used by the wallet header and
 * (later) the checkout sidebar to hint at applicable savings.
 */
export function useCustomerCreditsSummary() {
  const { token, customer } = useSession();

  return useQuery<CustomerCreditsSummary, Error>({
    queryKey: ["customer-credits-summary", customer?.id],
    enabled: !!token && !!customer?.id,
    queryFn: async () => {
      const res = await axios.get<{ data: CustomerCreditsSummary }>(
        `${API}/api/v2/customers/me/credits/summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data.data;
    },
  });
}
